// Public, read-only blog access. Uses supabaseAdmin scoped via WHERE status='published'
// so unauthenticated visitors can read the kunskaps-bibliotek during SSR/prerender.
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { supabaseAdmin } from "@/integrations/supabase/client.server";

export interface BlogPostSummary {
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  read_minutes: number;
  cover_emoji: string;
  published_at: string;
}

export interface BlogPostFull extends BlogPostSummary {
  body: string;
  author_name: string;
  author_role: string;
  seo_title: string | null;
  seo_description: string | null;
  updated_at: string | null;
}

const SUMMARY_FIELDS =
  "slug, title, excerpt, category, read_minutes, cover_emoji, published_at";

export const listBlogPosts = createServerFn({ method: "GET" }).handler(
  async () => {
    const { data, error } = await supabaseAdmin
      .from("blog_posts")
      .select(SUMMARY_FIELDS)
      .eq("status", "published")
      .order("published_at", { ascending: false });
    if (error) throw new Error(error.message);
    return { posts: (data ?? []) as BlogPostSummary[] };
  },
);

export const getBlogPost = createServerFn({ method: "GET" })
  .inputValidator((input) => z.object({ slug: z.string().min(1).max(200) }).parse(input))
  .handler(async ({ data }) => {
    const { data: post, error } = await supabaseAdmin
      .from("blog_posts")
      .select(
        "slug, title, excerpt, body, category, read_minutes, cover_emoji, published_at, updated_at, author_name, author_role, seo_title, seo_description",
      )
      .eq("status", "published")
      .eq("slug", data.slug)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!post) return { post: null as BlogPostFull | null, related: [] as BlogPostSummary[] };

    const { data: related } = await supabaseAdmin
      .from("blog_posts")
      .select(SUMMARY_FIELDS)
      .eq("status", "published")
      .neq("slug", data.slug)
      .order("published_at", { ascending: false })
      .limit(3);

    return {
      post: post as BlogPostFull,
      related: (related ?? []) as BlogPostSummary[],
    };
  });
